import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader } from './ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { normalizeOperationCategories } from './devisQuoteState';

const AUTOSAVE_MS = 400;

const newLine = () => ({
  id: null,
  type_label: '',
  taux_net: 0,
});

const collectCategoryIdsFromApi = (q) => {
  const raw = q.operation_category_ids;
  if (Array.isArray(raw)) {
    const out = [];
    for (const x of raw) {
      const s = x != null ? String(x).trim() : '';
      if (s && !out.includes(s)) out.push(s);
    }
    return out;
  }
  const out = [];
  const oc = q.operation_category_id;
  const oc2 = q.operation_category_id_2;
  const a = oc != null && String(oc).trim() !== '' ? String(oc) : '';
  const b = oc2 != null && String(oc2).trim() !== '' ? String(oc2) : '';
  if (a) out.push(a);
  if (b && !out.includes(b)) out.push(b);
  return out;
};

const normalizeFromApi = (q) => {
  let hour_lines = (q.hour_lines || []).map((l) => ({
    id: l.id || null,
    type_label: l.type_label ?? '',
    taux_net: l.taux_net ?? 0,
  }));

  if (hour_lines.length === 0) {
    hour_lines = [newLine()];
  }

  const hc = Number(q.headcount);
  const wy = Number(q.weeks_year);
  return {
    id: q.id,
    label: q.label ?? '',
    headcount: Number.isFinite(hc) ? hc : 1,
    weeks_year: Number.isFinite(wy) ? wy : 52,
    operation_category_ids: collectCategoryIdsFromApi(q),
    hour_lines,
  };
};

const buildQualificationPayload = (post) => ({
  label: (post.label || '').trim() || 'Poste',
  headcount: Number(post.headcount) || 1,
  weeks_year: Number(post.weeks_year) || 52,
  operation_category_ids: Array.isArray(post.operation_category_ids)
    ? post.operation_category_ids.filter((id) => id && String(id).trim())
    : [],
  hour_lines: post.hour_lines.map((l) => ({
    id: l.id || undefined,
    type_label: l.type_label || '',
    taux_net: Number(l.taux_net) || 0,
    mo_hours_key: '',
  })),
});

const DevisSalairesTab = ({
  isActive,
  token,
  apiBase,
  theme,
  onUnauthorized,
  canConfigure,
}) => {
  const [postes, setPostes] = useState([]);
  const [operationCategories, setOperationCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const hydratedRef = useRef(false);
  const postTimersRef = useRef({});
  const postesRef = useRef(postes);
  postesRef.current = postes;

  const handleApiError = useCallback(
    (error) => {
      if (error.response?.status === 401) {
        onUnauthorized?.();
        return;
      }
      const msg =
        error.response?.data?.detail ||
        error.message ||
        'Une erreur est survenue';
      toast.error(typeof msg === 'string' ? msg : 'Erreur serveur');
    },
    [onUnauthorized]
  );

  const load = useCallback(async () => {
    hydratedRef.current = false;
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [qualRes, catRes] = await Promise.all([
        axios.get(`${apiBase}/qualifications`, { headers }),
        axios.get(`${apiBase}/operation-categories`, { headers }),
      ]);
      const rows = Array.isArray(qualRes.data) ? qualRes.data : [];
      setPostes(rows.map(normalizeFromApi));
      setOperationCategories(normalizeOperationCategories(catRes.data));
    } catch (e) {
      handleApiError(e);
    } finally {
      setLoading(false);
      hydratedRef.current = true;
    }
  }, [apiBase, token, handleApiError]);

  useEffect(() => {
    return () => {
      Object.values(postTimersRef.current).forEach((t) => clearTimeout(t));
      postTimersRef.current = {};
    };
  }, []);

  const patchPostSilent = useCallback(
    async (post) => {
      if (!canConfigure || !token) return;
      try {
        const { data } = await axios.patch(
          `${apiBase}/qualifications/${post.id}`,
          buildQualificationPayload(post),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setPostes((prev) => prev.map((p) => (p.id === post.id ? normalizeFromApi(data) : p)));
      } catch (e) {
        handleApiError(e);
      }
    },
    [apiBase, token, canConfigure, handleApiError]
  );

  const scheduleSavePost = useCallback(
    (postId) => {
      if (!canConfigure || !hydratedRef.current || !token) return;
      if (postTimersRef.current[postId]) clearTimeout(postTimersRef.current[postId]);
      postTimersRef.current[postId] = setTimeout(() => {
        delete postTimersRef.current[postId];
        const post = postesRef.current.find((p) => p.id === postId);
        if (post) patchPostSilent(post);
      }, AUTOSAVE_MS);
    },
    [canConfigure, token, patchPostSilent]
  );

  useEffect(() => {
    if (!isActive || !token) return;
    load();
  }, [isActive, token, load]);

  const updatePost = (id, fn) => {
    setPostes((prev) => prev.map((p) => (p.id === id ? fn(p) : p)));
  };

  const handleLabelChange = (postId, value) => {
    updatePost(postId, (p) => ({ ...p, label: value }));
    scheduleSavePost(postId);
  };

  const handleOperationCategoriesChange = (postId, selectedIds) => {
    const next = Array.isArray(selectedIds)
      ? [...new Set(selectedIds.map((id) => String(id).trim()).filter(Boolean))]
      : [];
    updatePost(postId, (p) => ({ ...p, operation_category_ids: next }));
    scheduleSavePost(postId);
  };

  const handleLineField = (postId, lineIndex, field, raw) => {
    updatePost(postId, (p) => ({
      ...p,
      hour_lines: p.hour_lines.map((l, i) => {
        if (i !== lineIndex) return l;
        const v =
          field === 'type_label'
            ? raw
            : raw === '' || raw === null
              ? ''
              : Number(String(raw).replace(',', '.'));
        return { ...l, [field]: v };
      }),
    }));
    scheduleSavePost(postId);
  };

  const addLine = (postId) => {
    updatePost(postId, (p) => ({ ...p, hour_lines: [...p.hour_lines, newLine()] }));
    scheduleSavePost(postId);
  };

  const removeLine = (postId, lineIndex) => {
    const cur = postesRef.current.find((x) => x.id === postId);
    if (!cur || cur.hour_lines.length <= 1) {
      toast.message('Au moins un type d’heure est requis.');
      return;
    }
    updatePost(postId, (p) => ({
      ...p,
      hour_lines: p.hour_lines.filter((_, i) => i !== lineIndex),
    }));
    scheduleSavePost(postId);
  };

  const addPost = async () => {
    try {
      const { data } = await         axios.post(
        `${apiBase}/qualifications`,
        {
          label: 'Nouveau poste',
          headcount: 1,
          weeks_year: 52,
          hour_lines: [{ type_label: '', taux_net: 0, mo_hours_key: '' }],
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPostes((prev) => [...prev, normalizeFromApi(data)]);
      toast.success('Poste créé.');
    } catch (e) {
      handleApiError(e);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const pid = deleteTarget.id;
    if (postTimersRef.current[pid]) {
      clearTimeout(postTimersRef.current[pid]);
      delete postTimersRef.current[pid];
    }
    try {
      await axios.delete(`${apiBase}/qualifications/${pid}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPostes((prev) => prev.filter((p) => p.id !== pid));
      toast.success('Poste supprimé.');
    } catch (e) {
      handleApiError(e);
    } finally {
      setDeleteTarget(null);
    }
  };

  if (!isActive) return null;

  return (
    <div className="space-y-4">
      {!canConfigure && (
        <p className="text-sm" style={{ color: theme.textSecondary }}>
          Lecture seule. La modification des salaires requiert la permission{' '}
          <span className="font-medium" style={{ color: theme.text }}>
            devis.configure
          </span>
          .
        </p>
      )}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <p className="text-sm min-w-0" style={{ color: theme.textSecondary }}>
          Postes utilisés pour le chiffrage : plusieurs catégories d’opération possibles par poste (même
          logique que l’onglet Salaires du devis). Les modifications sont enregistrées automatiquement.
        </p>
        {canConfigure && (
          <Button
            type="button"
            size="lg"
            className="gap-2 shrink-0 font-semibold shadow-md transition-[filter,transform] hover:brightness-110 active:scale-[0.99] w-full sm:w-auto sm:min-w-[220px]"
            style={{ backgroundColor: theme.accent, color: '#fff' }}
            onClick={addPost}
          >
            <Plus className="h-5 w-5" strokeWidth={2.25} aria-hidden />
            Ajouter un poste
          </Button>
        )}
      </div>

      {loading && (
        <p className="text-sm" style={{ color: theme.textSecondary }}>
          Chargement…
        </p>
      )}

      {!loading && postes.length === 0 && (
        <p className="text-sm" style={{ color: theme.textSecondary }}>
          Aucun poste. {canConfigure ? 'Ajoutez un poste pour commencer.' : ''}
        </p>
      )}

      <div className="grid grid-cols-1 items-start gap-3 md:grid-cols-2 md:gap-4">
        {postes.map((post) => (
            <Card
              key={post.id}
              className="flex min-w-0 flex-col overflow-hidden rounded-xl border shadow-sm"
              style={{ borderColor: theme.border, backgroundColor: theme.surface }}
            >
              <CardHeader className="space-y-2 border-b p-3 sm:p-3.5" style={{ borderColor: theme.border }}>
                <div className="flex flex-col gap-2">
                  <div className="min-w-0 space-y-1">
                    <Label
                      htmlFor={`post-label-${post.id}`}
                      className="text-xs font-medium"
                      style={{ color: theme.textSecondary }}
                    >
                      Nom du poste
                    </Label>
                    <Input
                      id={`post-label-${post.id}`}
                      value={post.label}
                      onChange={(e) => handleLabelChange(post.id, e.target.value)}
                      disabled={!canConfigure}
                      className="h-8 w-full text-sm"
                      style={{
                        borderColor: theme.border,
                        backgroundColor: theme.inputBackground,
                        color: theme.text,
                      }}
                    />
                  </div>
                  <div className="min-w-0 space-y-1">
                    <Label
                      htmlFor={`post-opcats-${post.id}`}
                      className="text-xs font-medium"
                      style={{ color: theme.textSecondary }}
                    >
                      Catégories d’opération
                    </Label>
                    <select
                      id={`post-opcats-${post.id}`}
                      multiple
                      value={post.operation_category_ids || []}
                      onChange={(e) =>
                        handleOperationCategoriesChange(
                          post.id,
                          Array.from(e.target.selectedOptions, (opt) => opt.value)
                        )
                      }
                      disabled={!canConfigure}
                      size={Math.min(
                        Math.max(
                          operationCategories.length +
                            (post.operation_category_ids || []).filter(
                              (id) => !operationCategories.some((c) => c.id === id)
                            ).length,
                          3
                        ),
                        14
                      )}
                      className="w-full rounded-md border px-2 py-1 text-sm touch-manipulation"
                      style={{
                        borderColor: theme.border,
                        backgroundColor: theme.inputBackground,
                        color: theme.text,
                      }}
                      aria-describedby={`post-opcats-hint-${post.id}`}
                    >
                      {operationCategories.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.label || c.id}
                        </option>
                      ))}
                      {(post.operation_category_ids || [])
                        .filter((id) => !operationCategories.some((c) => c.id === id))
                        .map((id) => (
                          <option key={id} value={id}>
                            (Référentiel) {id.length > 12 ? `${id.slice(0, 12)}…` : id}
                          </option>
                        ))}
                    </select>
                    <p
                      id={`post-opcats-hint-${post.id}`}
                      className="text-[10px] leading-snug"
                      style={{ color: theme.textSecondary }}
                    >
                      Sélection multiple : maintenez <span className="font-medium" style={{ color: theme.text }}>Ctrl</span>{' '}
                      (Windows/Linux) ou <span className="font-medium" style={{ color: theme.text }}>Cmd</span> (Mac) pour en
                      cocher plusieurs. Le poste est pris en compte pour chaque catégorie choisie. Référentiel{' '}
                      <span className="font-medium" style={{ color: theme.text }}>
                        Paramètres → Opérations
                      </span>
                      .
                    </p>
                  </div>
                  {canConfigure && (
                    <div className="flex flex-wrap items-center justify-end gap-1.5 pt-0.5">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        aria-label="Supprimer le poste"
                        onClick={() => setDeleteTarget(post)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="flex flex-col gap-2 p-3 sm:p-3.5 pt-3">
                <div className="flex flex-wrap items-center justify-between gap-1.5">
                  <span className="text-xs font-semibold" style={{ color: theme.text }}>
                    Types d’heure
                  </span>
                  {canConfigure && (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="h-7 gap-1 px-2 text-xs"
                      onClick={() => addLine(post.id)}
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Ajouter
                    </Button>
                  )}
                </div>
                <div className="min-w-0 rounded-lg border" style={{ borderColor: theme.border }}>
                  <Table className="table-fixed" wrapperClassName="overflow-visible min-w-0">
                    <colgroup>
                      <col style={{ width: '60%' }} />
                      <col style={{ width: '40%' }} />
                    </colgroup>
                    <TableHeader>
                      <TableRow style={{ borderColor: theme.border }} className="hover:bg-transparent">
                        <TableHead
                          className="h-8 px-2 text-xs font-medium"
                          style={{ color: theme.text }}
                        >
                          Libellé
                        </TableHead>
                        <TableHead
                          className="h-8 px-2 text-right text-xs font-medium"
                          style={{ color: theme.text }}
                        >
                          €/h
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {post.hour_lines.map((line, idx) => (
                        <TableRow key={line.id || `${post.id}-line-${idx}`} style={{ borderColor: theme.border }}>
                          <TableCell className="p-1.5 align-middle" style={{ width: '60%' }}>
                            <Input
                              value={line.type_label}
                              onChange={(e) =>
                                handleLineField(post.id, idx, 'type_label', e.target.value)
                              }
                              disabled={!canConfigure}
                              placeholder="ex. Jour, nuit…"
                              title={line.type_label || undefined}
                              className="h-7 w-full min-w-0 text-xs"
                              style={{
                                borderColor: theme.border,
                                backgroundColor: theme.inputBackground,
                                color: theme.text,
                              }}
                            />
                          </TableCell>
                          <TableCell className="p-1.5 align-middle" style={{ width: '40%' }}>
                            <div className="flex min-w-0 items-center justify-end gap-1">
                              <Input
                                type="number"
                                min={0}
                                step={0.01}
                                inputMode="decimal"
                                className="h-7 min-w-0 max-w-full flex-1 px-1.5 text-right text-xs tabular-nums"
                                value={line.taux_net === '' ? '' : line.taux_net}
                                onChange={(e) =>
                                  handleLineField(post.id, idx, 'taux_net', e.target.value)
                                }
                                disabled={!canConfigure}
                                style={{
                                  borderColor: theme.border,
                                  backgroundColor: theme.inputBackground,
                                  color: theme.text,
                                }}
                              />
                              {canConfigure && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 shrink-0"
                                  aria-label="Supprimer ce type d’heure"
                                  onClick={() => removeLine(post.id, idx)}
                                >
                                  <Trash2 className="h-3.5 w-3.5 opacity-70" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
        ))}
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent style={{ borderColor: theme.border, backgroundColor: theme.surface }}>
          <AlertDialogHeader>
            <AlertDialogTitle style={{ color: theme.text }}>Supprimer ce poste ?</AlertDialogTitle>
            <AlertDialogDescription style={{ color: theme.textSecondary }}>
              Cette action retire le poste « {deleteTarget?.label || '—'} » et tous ses types d’heure du
              référentiel salaires.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={confirmDelete}
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DevisSalairesTab;
